/**
 * Phase 3.6 — RobustnessCard
 *
 * Renders the robustness profile of the recommended candidate when the
 * study objective was ``maximise_robustness``. Pulls every value from
 * ``diff.recommended.robustness_components`` — pure projection, no
 * derived math beyond display formatting.
 *
 * Hidden when ``robustness_components`` is null (i.e. non-robustness
 * studies). The card shows four stat tiles (mean, std, min..max range,
 * composite score) and a horizontal bar of the three perturbation
 * readings (baseline, plus, minus).
 */
import {
    Bar, BarChart, CartesianGrid, ResponsiveContainer,
    Tooltip, XAxis, YAxis,
} from "recharts";
import { Activity } from "lucide-react";

function fmt(n, digits = 3) {
    if (typeof n !== "number" || Number.isNaN(n)) return "—";
    return n.toFixed(digits);
}

function StatTile({ label, value, hint, testId }) {
    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-2 px-3 py-2"
            data-testid={testId}
        >
            <div className="text-[10px] uppercase tracking-wider text-ux-text-3">
                {label}
            </div>
            <div className="text-[15px] tabular-nums font-mono text-ux-text-1 mt-1">
                {value}
            </div>
            {hint ? (
                <div className="text-[10px] text-ux-text-3 mt-0.5">{hint}</div>
            ) : null}
        </div>
    );
}

export default function RobustnessCard({ diff }) {
    const rc = diff?.recommended?.robustness_components;
    if (!rc) return null;

    const lambda = typeof rc.lambda === "number" ? rc.lambda : 0.5;
    const ddof = typeof rc.ddof === "number" ? rc.ddof : null;
    const pct = typeof rc.perturbation_pct === "number"
        ? rc.perturbation_pct
        : null;
    const readings = Array.isArray(rc.readings_per_env)
        ? rc.readings_per_env
        : [];
    const chartData = readings.map((r) => ({
        env: r.tag === "baseline" ? "Baseline" :
             r.tag === "plus" ? "Wind +"   :
             r.tag === "minus" ? "Wind −"  : (r.tag || "?"),
        pk: typeof r.pk === "number" ? r.pk : 0,
    }));

    const range =
        typeof rc.pk_min === "number" && typeof rc.pk_max === "number"
            ? `${fmt(rc.pk_min, 3)} – ${fmt(rc.pk_max, 3)}`
            : "—";

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-4 space-y-3"
            data-testid="robustness-card"
        >
            <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-ux-accent" />
                <h3 className="text-[13px] font-medium text-ux-text-1">
                    Robustness profile
                </h3>
                <span
                    className="ml-auto text-[10px] uppercase tracking-wider text-ux-text-3"
                    title="Higher is better. Score rewards consistent Pk under environment perturbations."
                >
                    Composite
                </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <StatTile
                    label="Mean Pk"
                    value={fmt(rc.pk_mean, 3)}
                    testId="robustness-stat-mean"
                />
                <StatTile
                    label="Std (ddof=1)"
                    value={fmt(rc.pk_std, 3)}
                    testId="robustness-stat-std"
                />
                <StatTile
                    label="Range"
                    value={range}
                    testId="robustness-stat-range"
                />
                <StatTile
                    label="Score"
                    value={fmt(rc.robustness_score, 3)}
                    hint={`mean − ${lambda} × std`}
                    testId="robustness-stat-score"
                />
            </div>

            {chartData.length > 0 && (
                <div
                    style={{ width: "100%", height: 120 }}
                    data-testid="robustness-readings-chart"
                >
                    <ResponsiveContainer>
                        <BarChart
                            data={chartData}
                            layout="vertical"
                            margin={{ top: 4, right: 12, left: 8, bottom: 4 }}
                        >
                            <CartesianGrid stroke="var(--ux-border-1)" horizontal={false} />
                            <XAxis
                                type="number"
                                domain={[0, 1]}
                                tick={{ fontSize: 10, fill: "var(--ux-text-3)" }}
                                stroke="var(--ux-border-1)"
                                tickFormatter={(v) => v.toFixed(1)}
                            />
                            <YAxis
                                type="category"
                                dataKey="env"
                                width={80}
                                tick={{ fontSize: 10, fill: "var(--ux-text-2)" }}
                                stroke="var(--ux-border-1)"
                            />
                            <Tooltip
                                cursor={{ fill: "var(--ux-bg-2)" }}
                                formatter={(v) => [fmt(v, 3), "Pk"]}
                                contentStyle={{
                                    background: "var(--ux-bg-1)",
                                    border: "1px solid var(--ux-border-1)",
                                    fontSize: "11px",
                                }}
                            />
                            <Bar
                                dataKey="pk"
                                isAnimationActive={false}
                                fill="var(--ux-accent)"
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            <p
                className="text-[10px] text-ux-text-3 font-mono"
                data-testid="robustness-footer"
            >
                Perturbation: ±{pct === null ? "—" : `${(pct * 100).toFixed(0)}% wind`}
                {"   ·   "}λ = {lambda}
                {ddof !== null ? `   ·   ddof = ${ddof}` : ""}
            </p>
        </div>
    );
}
